/**
 * nextLiveAdapter — le prochain live de l'élève, pour la carte de son accueil.
 *
 * QUEL live : celui du bandeau legacy (`NextLiveSessionBanner.js`), `nextLiveFromToday` sur
 * `currentUser.live_sessions[].next_occurrences` (`data/utility/liveDay`) — la première occurrence
 * ni finalisée ni passée au calendrier. C'est aussi lui que « GO ! » ouvre (`liveSessionId` vers
 * `pre-call`), lu au moment du toucher.
 *
 * COMMENT il s'affiche : la ligne de `live_occurrences` (`getAllMyNextLiveSessions`) qui porte la
 * même séance à la même date, passée par l'adaptateur des séances du parent (`toUILiveSessions` :
 * tuteur, matières, retard, absence, remplaçant, rattrapage, paiement). Tant que cette liste n'est
 * pas lue, la séance du compte tient lieu de ligne : le même adaptateur, sans ce que seule
 * l'occurrence sait (l'ajout temporaire).
 *
 * Le paiement en attente vient du compte (`is_payment_blocked`, que `getUser` et
 * `getAllMyNextLiveSessions` posent tous les deux) : la séance porte alors `payment_blocked`.
 */
import moment from "moment"

import { nextLiveFromToday } from "../../../../../data/utility/liveDay"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import {
    toUILiveSessions,
    type ApiLiveOccurrence,
    type ApiLiveSessionNested,
    type ApiStudentForSubjects,
} from "../../parent/LiveSessions/liveSessionListAdapter"
import { isAbsenceReported } from "../../../ui/formatters/liveSessionFormatter"
import { type UILiveSession } from "../../../ui/types"

/** Une occurrence de `next_occurrences`, telle que `nextLiveFromToday` la rend. */
type ApiNextOccurrenceOfSession = {
    date?: string
    live_session_id?: string | number
    finalized_at?: string | null
}

export type ApiStudentLiveAccount = ApiStudentForSubjects & {
    live_sessions?: ApiLiveSessionNested[] | null
    is_payment_blocked?: boolean
    payment_url?: string | null
}

export type StudentNextLive = {
    session: UILiveSession
    /** La séance que « GO ! » ouvre dans `pre-call`. */
    liveSessionId: string | number
}

/** Les deux côtés écrivent « YYYY-MM-DD HH:mm » : normalisé pour que la jointure ne dérive pas. */
function dateKey(date: string | null | undefined): string | null {
    if (!date) return null
    const parsed = moment(date)
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : date
}

/** Le prochain live du compte, au sens du bandeau legacy. `now` : pour les tests. */
export function nextLiveOccurrenceOf(
    account: ApiStudentLiveAccount | null | undefined,
    now: moment.Moment = moment()
): ApiNextOccurrenceOfSession | null {
    return (nextLiveFromToday(account?.live_sessions, now) as ApiNextOccurrenceOfSession | undefined) ?? null
}

export function toStudentNextLive(
    account: ApiStudentLiveAccount | null | undefined,
    occurrences: ApiLiveOccurrence[] | null | undefined,
    apiSubjects: ApiSchoolSubject[] | null | undefined,
    now: moment.Moment = moment()
): StudentNextLive | null {
    const next = nextLiveOccurrenceOf(account, now)
    const liveSessionId = next?.live_session_id
    const key = dateKey(next?.date)
    if (next == null || liveSessionId == null || key == null) return null

    const options = {
        student: account,
        apiSubjects: apiSubjects ?? [],
        paymentBlocked: account?.is_payment_blocked === true,
    }

    const list = occurrences ?? []
    const row = list.find(
        (occurrence) =>
            String(occurrence?.live_session?.id) === String(liveSessionId) && dateKey(occurrence?.date) === key
    )
    if (row?.id != null) {
        // Toute la liste passe par l'adaptateur : le rattrapage se lit sur une ligne voisine.
        const session = toUILiveSessions(list, options).find((candidate) => String(candidate.id) === String(row.id))
        if (session != null) return { session, liveSessionId }
    }

    const liveSession = account?.live_sessions?.find((candidate) => String(candidate?.id) === String(liveSessionId))
    const fallback: ApiLiveOccurrence = {
        id: `${liveSessionId}:${key}`,
        date: next.date,
        tutor: liveSession?.tutor,
        live_session: liveSession,
    }
    const [session] = toUILiveSessions([fallback], options)
    return session != null ? { session, liveSessionId } : null
}

/** Les lignes courtes sous la carte du prochain live : deux au plus (Wajih, 18/10). */
export const HOME_FOLLOWING_LIVES = 2

/**
 * Les lives qui suivent le prochain, pour les lignes courtes de l'accueil : la lecture de « Mes
 * prochains lives » de l'ancien « Mon tutorat » (`live_occurrences`, par `toUILiveSessions`) et sa règle — une
 * séance où l'absence est signalée sans rattrapage posé n'y est pas. Seulement APRÈS le prochain live
 * (lu sur le compte, `toStudentNextLive`), jamais lui ; triés, coupés à `HOME_FOLLOWING_LIVES`.
 */
export function toStudentFollowingLives(
    account: ApiStudentLiveAccount | null | undefined,
    occurrences: ApiLiveOccurrence[] | null | undefined,
    apiSubjects: ApiSchoolSubject[] | null | undefined,
    next: StudentNextLive | null
): UILiveSession[] {
    if (next == null || !occurrences?.length) return []
    const heroAt = moment(next.session.date).valueOf()
    const heroKey = dateKey(next.session.date)
    return toUILiveSessions(occurrences, {
        student: account,
        apiSubjects: apiSubjects ?? [],
        paymentBlocked: account?.is_payment_blocked === true,
    })
        .filter((session) => !isAbsenceReported(session) || session.recoveryOccurrenceId != null)
        .filter((session) => String(session.id) !== String(next.session.id))
        .filter(
            (session) =>
                !(String(session.liveSessionId) === String(next.liveSessionId) && dateKey(session.date) === heroKey)
        )
        .map((session) => ({ session, at: moment(session.date).valueOf() }))
        .filter(({ at }) => at > heroAt)
        .sort((a, b) => a.at - b.at)
        .slice(0, HOME_FOLLOWING_LIVES)
        .map(({ session }) => session)
}
